// Health Wallet sync (SSE) service — the single encapsulation boundary for the
// workflow-progress SSE stream. Components import ONLY from here; they never touch
// `useGetSSEEventsQuery`, the `EventSource`, or `SSE_KEY` directly.
//
// Design (see project_hw_sse_central_service_plan):
//   • The connection ENGINE lives in the RTK `getSSEEvents` query (`onCacheEntryAdded`
//     in healthWalletSlice.ts): one EventSource, reconnect, and the once-per-connection
//     terminal side-effects (invalidate ['Patient','Onboarding']). "Use RTK properly."
//   • RTK ref-counts subscribers per key → ONE connection stays open while ≥1 consumer
//     is mounted and closes on the last unmount. That ref-counting IS the mount-scoping.
//   • This module exposes hooks so consumption is decoupled from RTK.
//
// Scope today: HW-screen mount (the anchor lives in HealthWalletMain). To make the
// connection app-level later, move the single `useHealthWalletSyncAnchor()` call to the
// app root (or add a store-level start/stop) — the engine and consumers don't change.
import { useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { healthWalletApi, useGetSSEEventsQuery, useGetOnboardingStatusQuery, TERMINAL_DATA_REQUEST_STATUS } from './healthWalletSlice';
import { useAuthProfile, useAppDispatch } from '../../app/hooks';

// Single STABLE key → all subscribers share one EventSource cache entry (RTK dedups by
// key). NEVER bump this: a different key forks a second connection and defeats the design.
export const SSE_KEY = 0;

function useAuthed(): boolean {
    const { authStatus, isLoading, isSuccess } = useAuthProfile();
    return !isLoading && isSuccess && authStatus === 'authenticated';
}

/**
 * Immediately clear the shared progress log. Call this on a Retry/Refresh BUTTON CLICK so the
 * previous run's messages vanish at once, instead of lingering until the first new SSE event
 * arrives (the engine's per-workflow reset only fires on that first event — fine for auto /
 * cross-client syncs, but feels laggy on a manual click). Pass the component's dispatch.
 */
export function clearSyncLog(dispatch: (action: any) => unknown): void {
    dispatch(healthWalletApi.util.updateQueryData('getSSEEvents', SSE_KEY, (draft: any) => {
        if (draft) draft.events = [];
    }));
}

// No dedicated "anchor". The connection's lifetime is owned by the REAL consumers below
// (RTK ref-counts subscribers to SSE_KEY): it opens when the first mounts and closes when
// the last unmounts. HealthWalletRecords calls useSyncRunning() (for the "Refreshing…"
// cue) and is mounted whenever the records screen is shown, so it keeps the
// connection alive there; the log + Settings add subscribers when shown. To make the
// connection app-level later, have an always-mounted app-root component consume one of
// these hooks (or add a store-level start/stop) — the engine + consumers don't change.

/**
 * CONSUMER (full event stream) — for the progress log. Re-renders on every event.
 */
export function useHealthWalletSync(): { events: any[] } {
    const authed = useAuthed();
    const { data } = useGetSSEEventsQuery(authed ? SSE_KEY : skipToken);
    return { events: data?.events ?? [] };
}

/**
 * CONSUMER (running flag) — the SERVER-AUTHORITATIVE "is a sync in flight?" signal, derived
 * from `onboardingStatus.dataRequestStatus` (pending|processing). Prefer this over the old
 * SSE-derived flag for anything that must survive a page reload: the SSE `events[]` buffer is
 * connection-scoped and starts EMPTY on reload / on another client, so a flag read off it goes
 * false even while a sync is genuinely running server-side. `dataRequestStatus` is persisted
 * and the engine keeps it fresh by invalidating ['Onboarding'] on workflow start AND terminal
 * (see getSSEEvents.onCacheEntryAdded in healthWalletSlice.ts) — so this flips on both edges.
 *
 * Does two jobs in one call so it's a drop-in for screen-level "is it running?" UI:
 *   1. subscribes to the SSE stream — NOT to read events, but to hold the ONE shared connection
 *      open (RTK ref-counts subscribers to SSE_KEY). A no-op `selectFromResult` means this
 *      subscription NEVER re-renders the caller on a progress line — only the connection matters.
 *   2. subscribes to getOnboardingStatus — which is ALSO what makes the engine's
 *      invalidateTags(['Onboarding']) actually refetch (invalidation is a no-op without a
 *      mounted subscriber) — and returns the durable running boolean from it.
 */
export function useSyncRunning(): boolean {
    const authed = useAuthed();
    const dispatch = useAppDispatch();
    // Read the connect-time status snapshot the SSE engine stashed (getIasRequestInitialState). Selecting it
    // (vs the old no-op) only re-renders us when the SNAPSHOT changes — on connect — never on a progress line
    // (those go to events[], not statusSnapshot), so the connection-holding role is unchanged.
    const { snapshot } = useGetSSEEventsQuery(authed ? SSE_KEY : skipToken, {
        selectFromResult: (r) => ({
            snapshot: (r.data as any)?.statusSnapshot as
                | { dataRequestId: string | null; dataRequestStatus: string | null }
                | undefined,
        }),
    });
    const { data } = useGetOnboardingStatusQuery(undefined, { skip: !authed });
    let status: string | undefined = data?.dataRequestStatus;

    // Recency-merge by run id. The snapshot is the SSE's read of the latest run AT SUBSCRIBE time; `data` is
    // the mount read. If they're the SAME run and the snapshot shows it reached a terminal state our onboarding
    // cache hasn't caught up to, the run finished in the mount-read↔subscribe gap — the snapshot wins (a run
    // only moves forward, so terminal beats a stale non-terminal). Different id ⇒ snapshot is a prior run →
    // defer to onboarding (kept fresh by live events). See project_hw_sse_central_service_plan.
    //
    // ⚠️ INVARIANT for ALL optimistic cache writers: NEVER patch a non-terminal dataRequestStatus without
    // ALSO setting the run's REAL dataRequestId (from the response / the pre-created auth intent). A
    // status-only patch that keeps the previous run's id FORGES this merge's race signature (snapshot
    // terminal + cache non-terminal, same id) → a spurious status refetch at click time — which once
    // manifested as the re-verify screen flashing before the Epic redirect. No mechanical guard exists here
    // ON PURPOSE (a "settled id" ref was designed and REJECTED: ias_request rows are legitimately REUSED —
    // discovery rows re-run, abandoned intents resurrect — so it could swallow a real reconcile). The
    // discipline IS the fix. See project_hw_seed_optimistic_interaction.
    const raceReconcile =
        !!snapshot && !!data &&
        snapshot.dataRequestId === data.dataRequestId &&
        !!snapshot.dataRequestStatus &&
        TERMINAL_DATA_REQUEST_STATUS.has(snapshot.dataRequestStatus) &&
        !TERMINAL_DATA_REQUEST_STATUS.has(status ?? '');
    if (raceReconcile) status = snapshot!.dataRequestStatus!;

    // The merge above fixes the progress bar instantly. But when the race fires, onboarding (hasRecords, the
    // raw status other components read) AND records are stale too — so pull authoritative state ONCE per run.
    // This is the ONLY refetch the snapshot triggers, and only in the (rare) race; the common connect where
    // snapshot == onboarding never enters here, which is what kills the every-load double status call.
    const reconciledRef = useRef<string | null>(null);
    useEffect(() => {
        if (raceReconcile && snapshot?.dataRequestId && reconciledRef.current !== snapshot.dataRequestId) {
            reconciledRef.current = snapshot.dataRequestId;
            dispatch(healthWalletApi.util.invalidateTags(['Patient', 'Onboarding']));
        }
    }, [raceReconcile, snapshot?.dataRequestId, dispatch]);

    return status === 'pending' || status === 'processing';
}
